import React from 'react';
import moment from 'moment';
import { Badge, Button, Checkbox, Empty, Icon, Popover } from 'antd';
import { connect } from 'react-redux';

import {
  Card,
  CardActions,
  CardCategory,
  CardContent,
  CardDescription,
  Cards,
  CardTime,
  CardTitle,
  CardUrl,
  Container,
  FavIcon,
  MainContainer,
  LoadingSkeleton,
} from './styles';
import { copyToClipBoard } from '~/utils/copy-to-clipboard';
import * as linksActions from './actions';
import CategoriesModal from './Modals/CategoriesModal';
import { ModuleHeader } from '~/components/ModuleHeader';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  LINKS_FILTERS_KEY,
  getDefaultLinksFilters,
  countActiveLinksFilters,
  filterLinks,
} from './helpers';

const Links = ({
  openDrawer,
  links,
  openCategoriesModal,
  linkCategories,
  isLoading,
}) => {
  // Filters kept across refreshes for a few minutes (per tenant/user/page).
  // Links are filtered client-side, so nothing needs to be reloaded.
  var persisted = usePersistedFilters(LINKS_FILTERS_KEY, getDefaultLinksFilters);
  var filter = persisted.filters;
  var setFilter = persisted.setFilters;

  var filteredLinks = filterLinks(links, filter);

  var CATEGORY_COLORS = ['#722ed1', '#1890ff', '#52c41a', '#faad14', '#f5222d', '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb', '#a0d911'];

  var getCategoryCount = function(categoryId) {
    return links.filter(function(link) { return link.link_category_id === categoryId; }).length;
  };

  var getCategoryColor = function(index) {
    return CATEGORY_COLORS[index % CATEGORY_COLORS.length];
  };

  // Build a color map by category id for use in cards
  var categoryColorMap = {};
  linkCategories.forEach(function(cat, idx) {
    categoryColorMap[cat.id] = getCategoryColor(idx);
  });

  var truncateUrl = function(url) {
    if (!url) return '';
    var clean = url.replace(/^https?:\/\//, '').replace(/\/$/, '');
    return clean.length > 40 ? clean.substring(0, 40) + '...' : clean;
  };

  var hasActiveCategories = filter.categories.length > 0;
  var filterCount = countActiveLinksFilters(filter);

  var handleClearAll = function() {
    persisted.resetFilters();
  };

  var selectedCategoryNames = linkCategories
    .filter(function(c) { return filter.categories.includes(c.id); })
    .map(function(c) { return c.name; });

  var pills = [];
  if (hasActiveCategories) {
    pills.push({
      key: 'categories',
      // Categories load async: until they do, a restored filter has no names yet.
      label: selectedCategoryNames.length === 0
        ? filter.categories.length + (filter.categories.length === 1 ? ' categoria' : ' categorias')
        : selectedCategoryNames.length <= 2
        ? selectedCategoryNames.join(', ')
        : selectedCategoryNames.length + ' categorias',
      color: '#722ed1',
      icon: 'tags',
      onRemove: function() { setFilter({ categories: [] }); },
    });
  }
  if (filter.title) {
    pills.push({
      key: 'search',
      label: 'Busca: ' + filter.title,
      color: '#722ed1',
      icon: 'search',
      onRemove: function() { setFilter({ title: '' }); },
    });
  }

  var restoredSummary = pills.map(function(pill) { return pill.label; }).join(' · ');

  return (
    <Container>
      <ModuleHeader
        breadcrumbs={['Links de atalho']}
        title={'Links de atalho (' + (hasActiveCategories || filter.title ? filteredLinks.length + '/' : '') + links.length + ')'}
        search={
          <Search
            value={filter.title}
            onChange={function(e) { setFilter({ title: e.target.value }); }}
            placeholder="Pesquisar"
          />
        }
        center={
          <>
            <Popover
              trigger="click"
              placement="bottomRight"
              content={
                <div style={{ width: 300 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                    {!persisted.isDefault && (
                      <a style={{ fontSize: 12, color: '#f5222d' }} onClick={handleClearAll}>
                        Limpar tudo
                      </a>
                    )}
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Categorias</div>
                      <a style={{ fontSize: 12, color: '#1890ff' }} onClick={openCategoriesModal}>
                        <Icon type="edit" style={{ marginRight: 4, fontSize: 11 }} />Editar
                      </a>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
                      {linkCategories.map(function(category, idx) {
                        var isChecked = filter.categories.includes(category.id);
                        var color = getCategoryColor(idx);
                        var count = getCategoryCount(category.id);
                        return (
                          <div
                            key={category.id}
                            onClick={function() {
                              if (isChecked) {
                                setFilter({
                                  categories: filter.categories.filter(function(id) { return id !== category.id; }),
                                });
                              } else {
                                setFilter({
                                  categories: filter.categories.concat([category.id]),
                                });
                              }
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                              border: '1.5px solid ' + (isChecked ? color : '#e8e8e8'),
                              background: isChecked ? color + '0a' : '#fafafa',
                              transition: 'all 0.2s',
                            }}
                          >
                            <span style={{ fontSize: 12, color: isChecked ? '#262626' : '#8c8c8c' }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', marginRight: 8 }} />
                              {category.name}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isChecked ? color : '#bfbfbf' }}>{count}</span>
                              {isChecked && <Icon type="check" style={{ color: color, fontSize: 12 }} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              }
            >
              <Badge
                count={filterCount}
                offset={[-4, 0]}
                style={{ backgroundColor: '#722ed1' }}
              >
                <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
              </Badge>
            </Popover>
            <Button icon="plus" shape="circle" onClick={function() { openDrawer(); }} />
          </>
        }
        advanced={
          pills.length > 0
            ? React.createElement(FilterPillsBar, {
                pills: pills,
                onClearAll: handleClearAll,
              })
            : undefined
        }
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored}
        summary={restoredSummary}
        onReset={handleClearAll}
        onDismiss={persisted.dismissRestored}
      />
      <MainContainer style={{ width: '100%' }}>
        <Cards hasNotLinks={filteredLinks.length === 0}>
          {isLoading ? (
            Array(10)
              .fill(1)
              .map(function(_, index) { return <LoadingSkeleton key={index} />; })
          ) : filteredLinks.length === 0 ? (
            <Empty
              description="Nenhum link encontrado."
              style={{ marginTop: 100 }}
            />
          ) : (
            filteredLinks.map(function(link) {
              var catColor = categoryColorMap[link.link_category_id] || '#8c8c8c';
              return (
                <Card key={link.id} onClick={function() { openDrawer(link); }}>
                  <FavIcon
                    src={'https://www.google.com/s2/favicons?sz=32&domain=' + link.address}
                  />
                  <CardContent>
                    <CardTitle
                      href={link.address}
                      target="_blank"
                      onClick={function(event) { event.stopPropagation(); }}
                    >
                      {link.title}
                    </CardTitle>
                    <CardUrl>{truncateUrl(link.address)}</CardUrl>
                    {link.description && <CardDescription>{link.description}</CardDescription>}
                    <CardCategory color={catColor}>{link.category && link.category.name}</CardCategory>
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      icon="copy"
                      onClick={function(event) { event.stopPropagation(); copyToClipBoard(link.address); }}
                      title="Copiar link"
                    />
                    <Button
                      size="small"
                      icon="link"
                      onClick={function(event) { event.stopPropagation(); window.open(link.address, '_blank'); }}
                      title="Abrir link"
                    />
                  </CardActions>
                  <CardTime>
                    {moment(link.created_at).fromNow()}
                  </CardTime>
                </Card>
              );
            })
          )}
        </Cards>
      </MainContainer>
      <CategoriesModal />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  links: state.links.list,
  isLoading: state.links.isLoading,
  linkCategories: state.links.categories,
});

const mapDispatchProps = (dispatch) => ({
  openDrawer: (data) => dispatch(linksActions.openDrawer(data)),
  openCategoriesModal: () => dispatch(linksActions.openCategoriesModal()),
});

export default connect(mapStateToProps, mapDispatchProps)(Links);
